import { forwardRef } from 'react';
import  { FunctionComponent } from 'react';
import NumberFormat from 'react-number-format';
import { toNumber, toPattern } from 'vanilla-masker';

export type InputProps = {
    size?: InputSize;
    placeholder?: string;
    info?: string;
    value: string;
    name: string;
    mask?: MaskType;
    prefix?: string;
    sufix?: string;
    maxLength?: number;
    className?: string;
    errorMessage?: string;
    decimalScale?: number;
    sendRawValue?: boolean;
    type?: 'email';
    ref?: any;
    id?: string;
    onChange:(value: any) => void;
    onBlur?:() => void;
    onFocus?:() => void;
}

type InputSize = 'normal' | 'large';
type MaskType = 'money' | 'cpf' | 'cnpj' | 'phone';

const getFormattedValue = (value: string, mask: MaskType) => {
    if (mask === 'cpf') {
        return toPattern(value || '', '999.999.999-99');
    }

    if (mask === 'cnpj') {
        return toPattern(value || '', '99.999.999/9999-99');
    }

    if (mask === 'phone') {
        const regex = /(\(|-|\)|\s)/gi;
        const formattedValue = value?.replace(regex, "");
        const phonePattern = formattedValue?.length > 10
            ? '(99) 99999-9999' : '(99) 9999-9999';

        return toPattern(value || '', phonePattern);
    }

    return value;
};

const Input: FunctionComponent<InputProps> = forwardRef((props: InputProps, ref: any) => {
    const {
        size, placeholder, info, onChange, onBlur, onFocus, value, mask, prefix, sufix,
        className, errorMessage, sendRawValue, decimalScale = 2, type = 'text', maxLength, id
    } = props;

    const classes = ['input-text'];
    const containerClasses = ['input-container'];

    if (size === 'large') {
        classes.push('lg');
    }

    if (className) {
        containerClasses.push(className);
    }

    if (value && value !== '') {
        containerClasses.push('filled');
    }

    const formattedValue = getFormattedValue(value, mask);

    const numericMasks = ['money', 'cpf', 'cnpj', 'phone'];

    const handleChange = (event: any) => {
        if (!mask || sendRawValue === false) {
            return onChange(event);
        }

        if (mask === 'phone') {
            event.target.value = getFormattedValue(event.target.value, 'phone');
        } else {
            event.target.value = toNumber(getFormattedValue(event.target.value, mask));
        }

        onChange(event);
    };

    const handleMoneyChange = ({ floatValue }: any) => {
        onChange({ target: { value: floatValue } });
    };

    return (
        <div className="input-text-container">
            <div className={containerClasses.join(' ')}>
                <label>
                    { mask === 'money' && (
                        <NumberFormat
                            className={classes.join(' ')}
                            onValueChange={handleMoneyChange}
                            value={Number(value) === 0 ? null : value}
                            prefix={prefix}
                            suffix={sufix}
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={decimalScale}
                            fixedDecimalScale
                            allowNegative={false}
                            isAllowed={(values) => (!values.value.match(/^0+/))}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            type="tel"
                            id={id || ''}
                        />
                    ) }

                    { mask !== 'money' && (
                        <input
                            type={(mask != null && (numericMasks.indexOf(mask) !== -1) ? 'tel' : type)}
                            maxLength={maxLength}
                            className={classes.join(' ')}
                            onChange={handleChange}
                            value={formattedValue}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            ref={ref}
                            id={id || ''}
                        />
                    ) }

                    { placeholder && <div className="label">{ placeholder }</div> }
                </label>

                <span className="bottom" />
                { errorMessage && <span className="error-message">{ errorMessage }</span>}
                { info && <div className="info">{ info }</div> }
            </div>
        </div>
    );
})

export default Input;

